<template>
  <div class="nebDatePicker"
    v-neb-click-outside="onClickOutside"
  >
    <div class="nebDatePicker-select"
      :class="{
        placeholder: !hasValue,
        disabled,
        readOnly,
        open: showDropdown,
        dirty: highlightDirty && defaultValue !== undefined && !isEqual(value, defaultValue)
      }"
      @click="toggleShowDropdown"
    >
      <span>{{ valueLabel }}</span>

      <neb-icon name="caretDown"
        height="12px"
        width="12px"
        v-show="clearable ? !readOnly && (!hasValue || isEqual(value, defaultValue)) : !readOnly"
      ></neb-icon>

      <neb-icon name="x"
        height="12px"
        width="12px"
        v-show="!readOnly && !disabled && clearable && hasValue && !isEqual(value, defaultValue)"
        @click.native="onClear"
      ></neb-icon>
    </div>

    <div class="nebDatePicker-dropdown"
      v-show="showDropdown"
      ref="dropdown"
    >
      <div class="nebDatePicker-defaultOptions"
        v-show="defaultOptions.length > 0"
      >
        <div class="nebDatePicker-defaultOptions-option"
          v-for="(opt, idx) in defaultOptions"
          :key="idx"
          :class="{
            selected: optionIsSelected(opt)
          }"
          @click="$emit('input', opt.value)"
        >
          <span>{{ opt.label }}</span>
        </div>
      </div>

      <div class="nebDatePicker-calendar">
        <section v-if="mode === 'single'"
          class="nebDatePicker-calendar-single"
        >
          <neb-month :year="formData.year"
            :month="formData.month"
            :value="value"
            mode="single"
            :disableDayFunc="disableDayFunc"
            @previousMonth="onPrevMonth"
            @nextMonth="onNextMonth"
            @input="val => $emit('input', val)"
          ></neb-month>
        </section>

        <section class="nebDatePicker-calendar-range"
          v-if="mode === 'range'"
        >
          <neb-month :year="formData.year"
            :month="formData.month - 1"
            mode="range"
            :value="localValue"
            hideNext
            :disableDayFunc="disableDayFunc"
            @previousMonth="onPrevMonth"
            @input="onRangeInput"
          ></neb-month>

          <div class="nebDatePicker-calendar-range-divider"></div>

          <neb-month :year="formData.year"
            :month="formData.month"
            mode="range"
            :value="localValue"
            hidePrevious
            :disableDayFunc="disableDayFunc"
            @nextMonth="onNextMonth"
            @input="onRangeInput"
          ></neb-month>
        </section>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import { isEqual } from 'lodash'

import NebMonth from './Month'
import NebIcon from '../../Icon'

export default {
  name: 'NebDatePicker',

  components: {
    NebMonth,
    NebIcon
  },

  props: {
    value: {
      type: Array,
      default: () => [ null, null ]
    },
    /**
     * Control mode. One of ['single', 'range'].
     */
    mode: {
      type: String,
      default: 'single'
    },
    placeholder: {
      type: String,
      default: 'Select Date(s)'
    },
    disabled: {
      type: Boolean,
      default: false
    },
    readOnly: {
      type: Boolean,
      default: false
    },
    prefix: {
      type: String,
      default: ''
    },
    /**
     * An Array of objects where each object is a default option.
     *
     * Each object has 2 keys:
     * - label {String}: The human readable option label.
     * - value {Array[String|Number]}: This should be the value for the option. The first value in the array is the
     * start date, and the second value is the end date. Each of these values can be either a string or integer. If
     * the value is a string, it must be an iso8601 date string. If an nteger is provided, it specifies a relative
     * date. So, providing a negative integer means a numbeer of days in the past whil a positive number means a
     * number of days in the future. 0 is special, as it means the current day.
     *
     * ### Example:
     * ```javascript
     * // This would make the start date 7 days ago and the end date today.
     * {
     *   label: 'Last 7 days',
     *   value: [ -7, 0 ]
     * }
     *
     * // This would make the start date 2020-01-01 and the end date 2020-02-01
     * {
     *   label: 'January',
     *   value: [ '2020-01-01', '2020-02-01' ]
     * }
     *
     * // This would make the start date jan 1st and the end date 30 days from today.
     * {
     *   label: 'YTD',
     *   value: [ '2020-01-01', 30 ]
     * }
     * ```
     */
    defaultOptions: {
      type: Array,
      default: () => []
    },
    /**
     * A function called for each day to determine if the day should be disabled or not.
     *
     * Type signatue (Number, Number, Number) => Boolean
     *
     * The function recieves 3 arguments in order: the year, the month, and the day of the month. The function should
     * retun true if the day should be disabled, false otherwise.
     */
    disableDayFunc: {
      type: Function
    },
    highlightDirty: {
      type: Boolean,
      default: false
    },
    defaultValue: {
      type: Array
    },
    // If enabled, will add an "x" icon to the right side of thee input that, when clicked, will emit a 'clar' event.
    clearable: {
      type: Boolean,
      default: false
    }
  },

  data: () => ({
    formData: {
      year: moment().year(),
      month: moment().month()
    },
    showDropdown: false,
    localValue: [ null, null ]
  }),

  computed: {
    humanizedMonth () {
      return moment().month(this.formData.month).format('MMMM')
    },
    valueLabel () {
      if (!this.hasValue) {
        return this.prefix ? `${this.prefix}: ${this.placeholder}` : this.placeholder
      }

      const [ start, end ] = this.value

      if (this.defaultOptions) {
        const option = this.defaultOptions
          .find(opt => isEqual(opt.value, this.value))

        if (option) {
          return this.prefix ? `${this.prefix}: ${option.label}` : option.label
        }
      }

      if (this.mode === 'single') {
        const l = moment(start).format('MM/DD/YYYY')
        return this.prefix ? `${this.prefix}: ${l}` : l
      } else {
        const l = `${moment(start).format('MM/DD/YYYY')} - ${moment(end).format('MM/DD/YYYY')}`
        return this.prefix ? `${this.prefix}: ${l}` : l
      }
    },
    hasValue () {
      const [ start, end ] = this.value

      return !!(start || end)
    }
  },

  methods: {
    onRangeInput (value) {
      this.localValue = value

      if (value[1] !== null) {
        this.$emit('input', value)
      }
    },
    isEqual: isEqual,
    onClear (e) {
      e.stopPropagation()
      e.preventDefault()

      this.$emit('clear')
    },
    optionIsSelected (opt) {
      return isEqual(this.value, opt.value)
    },
    toggleShowDropdown () {
      if (this.disabled || this.readOnly) { return }
      this.showDropdown = !this.showDropdown
    },
    onClickOutside () {
      if (this.showDropdown) {
        this.showDropdown = false
      }
    },
    onPrevMonth () {
      const { month } = this.formData

      if (month === 0) {
        this.formData.year = this.formData.year - 1
        this.formData.month = 11
      } else {
        this.formData.month = this.formData.month - 1
      }
    },
    onNextMonth () {
      const { month } = this.formData

      if (month === 11) {
        this.formData.year = this.formData.year + 1
        this.formData.month = 0
      } else {
        this.formData.month = this.formData.month + 1
      }
    },
    updateDropdownPosition () {
      const dropdownDimensions = this.$refs.dropdown.getBoundingClientRect()

      if (dropdownDimensions.right === 0) { return }

      const bodyDimensions = document.body.getBoundingClientRect()

      if (dropdownDimensions.right >= bodyDimensions.right - 10) {
        const diff = dropdownDimensions.right - (bodyDimensions.right - 10)
        if (diff !== 0) {
          this.$refs.dropdown.style.right = `0px`
        }
      }
    }
  },

  watch: {
    showDropdown (newValue) {
      if (newValue) {
        this.$nextTick(this.updateDropdownPosition)
      }
    },
    value (newValue) {
      if (this.mode === 'range') {
        this.localValue = newValue
      }
    }
  },

  mounted () {
    if (this.mode === 'range') {
      this.localValue = this.value
    }
  }
}
</script>

<style lang="scss">
@import '../../style.scss';

.nebDatePicker {
  height: 34px;
  width: 100%;
  position: relative;

  &-defaultOptions {
    box-sizing: border-box;
    overflow-x: hidden;
    overflow-y: scroll;
    width: 150px;
    border-right: solid 1px $pinkish-grey;
    padding-top: 5px;

    &-option {
      padding: 0 15px;
      cursor: pointer;

      &:hover,
      &.selected {
        background-color: $duck-egg-blue;
      }

      >span {
        font-family: $defaultFont;
        font-size: 12px;
        font-weight: normal;
        font-stretch: normal;
        font-style: normal;
        line-height: 2.5;
        letter-spacing: normal;
        color: $slate-grey;
      }
    }
  }

  &-select {
    height: 34px;
    background-color: $white;
    border-radius: 3px;
    border: solid 1px $light-grey-blue;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    padding-left: 15px;
    padding-right: 15px;
    color: $slate-grey;
    font-family: $defaultFont;
    font-size: 12px;
    cursor: pointer;
    position: relative;

    &.dirty {
      background-color: #e8f4fc;
      border: 1px solid #a0c3d2;
    }

    .nebIcon {
      color: $slate-grey;
      opacity: 0.7;
    }

    &.placeholder {
      color: $warm-grey;
    }

    &.disabled {
      color: $pinkish-grey;
      background-color: $white-four;
      cursor: not-allowed;
      border: solid 1px $pinkish-grey;

      .nebIcon {
        color: $warm-grey;
      }
    }

    &.readOnly {
      border-top: none;
      border-left: none;
      border-right: none;
      border-bottom: 2px solid $warm-grey;
      cursor: initial;
      border-radius: initial;
    }

    &.open {
      box-shadow: 0 0 3px 0 $tiffany-blue;
      border: solid 1px $tiffany-blue;
    }
  }

  &-dropdown {
    background-color: $white;
    box-sizing: border-box;
    border-radius: 3px;
    box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.5);
    border: solid 1px $pinkish-grey;
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: stretch;
    position: absolute;
    top: 39px;
    z-index: 2001;
  }

  &-calendar {
    padding: 1.5rem 0;

    &-range,
    &-single {
      box-sizing: border-box;
      overflow: hidden;
      display: flex;
      flex-direction: row;
      justify-content: flex-start;
      align-items: stretch;
    }

    &-range {
      &-divider {
        width: 1px;
        background-color: $pinkish-grey;
      }
    }
  }
}
</style>
